import {
  Alert,
  Button,
  Form,
  Input,
  notification,
  Select
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ArrowLeft } from 'lucide-react';
import SeeWardLogo from '../../../assets/image/logoSeeWard.png';
import workspaceService from '../../../service/workspace.service';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateDefaultWorkspace } from '../../../redux/auth/actions';
import { selectCurrentAdmin } from '../../../redux/auth/selectors';
import WorkspaceAccountMenu from './WorkspaceAccountMenu';

const CreateWorkspaceFeature = () => {
  const [form] = Form.useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const currentUser = useSelector(selectCurrentAdmin) || {};
  const activeWorkspace = useSelector((state: any) => state.auth?.current?.default_workspace);
  const [entities, setEntities] = useState<{ value: number; label: string }[]>([]);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    workspaceService.getWorkspaceList().then((list: any) => {
      if (cancelled) return;
      const options = (Array.isArray(list) ? list : [])
        .filter((n: any) => n.kind === 'entity' && n.can_create_workspace)
        .map((n: any) => ({ value: n.id, label: n.display_path ?? n.name }));
      setEntities(options);
      const preferred = options.find((o: any) => o.value === activeWorkspace?.entity_id);
      const initial = preferred ?? (options.length === 1 ? options[0] : undefined);
      if (initial) form.setFieldsValue({ entityId: initial.value });
      setEntitiesLoaded(true);
    }).catch(() => setEntitiesLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [form, activeWorkspace?.entity_id]);

  // No candidate entity: hide the picker and let the server place the workspace under the
  // active entity (it enforces the rights).
  const noEntity = entitiesLoaded && entities.length === 0;

  const handleCreateWorkspace = async (values: any) => {
    try {
      const res = await workspaceService.createWorkspace(values.workspaceName, values.description, values.entityId);
      // New r/v2/workspaces API returns { id, name, slug, ... } directly
      if (res && res.id) {
        // Update Redux + localStorage directly — avoids the old `workspace/active`
        // endpoint which only knows about the old workspace table
        await dispatch(updateDefaultWorkspace(res));
        const existingAuth = JSON.parse(sessionStorage.getItem('auth') || '{}');
        const updated = {
          ...existingAuth,
          current: {
            ...existingAuth.current,
            default_workspace: res
          }
        };
        sessionStorage.setItem('auth', JSON.stringify(updated));
        form.resetFields();
        window.location.href = '/';
      }
      setIsButtonDisabled(true);
    } catch (error) {
      notification.error({
        message: 'Creation Failed',
        description: 'There was an error creating the workspace. Please try again.'
      });
    }
  };

  const handleFormChange = () => {
    const { workspaceName, description } = form.getFieldsValue();
    setIsButtonDisabled(!workspaceName);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-5 mx-5">
        <Button
          type="default"
          href="/"
          icon={<ArrowLeft className="mt-1" size={17} />}
        >
          Back to SeeWard
        </Button>
        <WorkspaceAccountMenu />
      </div>

      <div
        className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8"
      >
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            alt="SeeWard"
            src={SeeWardLogo}
            className="mx-auto h-10 w-auto"
          />
          <h2
            className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900"
          >
            Create a New Workspace
          </h2>
          <p className="text-center">
            Workspaces are shared environments where teams can work on projects, vulnerabilities, and issues.
          </p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleCreateWorkspace}
              onFieldsChange={handleFormChange}
            >
              {noEntity && (
                <Alert
                  type="info"
                  showIcon
                  className="mb-4"
                  message="The workspace will be created under your current entity."
                />
              )}
              <Form.Item
                label="Entity"
                name="entityId"
                hidden={entities.length <= 1}
                rules={[{ 
                  required: entities.length > 1, 
                  message: 'Please choose an entity' 
                }]}
              >
                <Select
                  size="large"
                  placeholder="Choose an entity"
                  options={entities}
                  disabled={!currentUser.admin_kc}
                />
              </Form.Item>
              <Form.Item
                label="Workspace Name"
                name="workspaceName"
                rules={[{
                  required: true,
                  message: 'Please enter the workspace name'
                }]}
              >
                <Input
                  size="large"
                  placeholder="Enter workspace name"
                  disabled={!currentUser.admin_kc}
                />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
              >
                <TextArea
                  disabled={!currentUser.admin_kc}
                  size="large"
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  placeholder="Enter workspace description"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="w-full"
                  size="large"
                  disabled={isButtonDisabled || !currentUser.admin_kc}
                >
                  Create Workspace
                </Button>
              </Form.Item>
              {!currentUser.admin_kc && <p
                className="text-center text-red-500"
              >You are not allowed to create a workspace.</p>}
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateWorkspaceFeature;
